<script>
export default {
  name: "RouletteWheel",
  props: {
    pockets: {
      type: Array,
      required: true,
      validator: pockets => pockets.length > 1
    },
    rewards: {
      type: Array,
      required: true,
      validator: rewards => rewards.length > 0
    },
    remainingSpins: {
      type: Number,
      default: 1
    }
  },
  data() {
    return {
      rotation: 0,
      ballRotation: 0,
      isSpinning: false,
      selectedPocket: null,
      selectedReward: null
    };
  },
  computed: {
    segmentAngle() {
      return 360 / this.pockets.length;
    },
    wheelStyle() {
      const colorStops = this.pockets
        .map((pocket, index) => {
          const start = index * this.segmentAngle;
          const end = start + this.segmentAngle;
          return `${pocket.color} ${start}deg ${end}deg`;
        })
        .join(", ");

      return {
        "--wheel-rotation": `${this.rotation}deg`,
        background: `conic-gradient(from -${this.segmentAngle /
          2}deg, ${colorStops})`
      };
    },
    ballStyle() {
      return {
        "--ball-rotation": `${this.ballRotation}deg`
      };
    },
    actionLabel() {
      if (this.isSpinning) return "ลูกบอลกำลังหมุน";
      if (this.remainingSpins < 1) return "ใช้สิทธิ์แล้ว";
      return "ปล่อยลูกบอล";
    }
  },
  methods: {
    pocketStyle(index, pocket) {
      const angle = index * this.segmentAngle;

      return {
        color: pocket.textColor || "#f8ead0",
        transform: `rotate(${angle}deg) translateY(calc(var(--number-radius) * -1))`
      };
    },
    railPinStyle(index) {
      return {
        transform: `rotate(${index *
          30}deg) translateY(calc(var(--rail-pin-radius) * -1))`
      };
    },
    spin() {
      if (this.isSpinning || this.remainingSpins < 1) return;

      const winnerIndex = Math.floor(Math.random() * this.pockets.length);
      const rewardIndex = Math.floor(Math.random() * this.rewards.length);
      const normalizedRotation = ((this.rotation % 360) + 360) % 360;
      const pocketOffset = winnerIndex * this.segmentAngle;
      const extraOffset = Math.floor(Math.random() * 280) + 40;

      this.selectedPocket = this.pockets[winnerIndex];
      this.selectedReward = this.rewards[rewardIndex];
      this.isSpinning = true;
      this.rotation += 360 * 8 + extraOffset - normalizedRotation;
      this.ballRotation -= 360 * 11 + pocketOffset + extraOffset * 0.5;
      this.$emit("spin-start");

      if (window.navigator && window.navigator.vibrate) {
        window.navigator.vibrate([22, 30, 22]);
      }
    },
    handleTransitionEnd(event) {
      if (
        !this.isSpinning ||
        event.target !== event.currentTarget ||
        event.propertyName !== "transform"
      ) {
        return;
      }

      this.isSpinning = false;
      this.$emit("spin-complete", {
        ...this.selectedReward,
        pocketNumber: this.selectedPocket.number
      });
    }
  }
};
</script>

<template>
  <section class="roulette-stage" aria-label="โต๊ะรูเล็ตยุโรป">
    <div class="roulette-stage__shadow"></div>

    <div class="roulette-machine">
      <div class="roulette-wood">
        <span
          v-for="railPinIndex in 12"
          :key="`rail-pin-${railPinIndex}`"
          class="rail-pin"
          :style="railPinStyle(railPinIndex - 1)"
        ></span>

        <div class="roulette-rail">
          <div
            class="ball-orbit"
            :class="{ 'ball-orbit--spinning': isSpinning }"
            :style="ballStyle"
            aria-hidden="true"
          >
            <span class="roulette-ball"></span>
          </div>

          <div class="roulette-pocket-ring">
            <div
              class="roulette-disc"
              :class="{ 'roulette-disc--spinning': isSpinning }"
              :style="wheelStyle"
              @transitionend="handleTransitionEnd"
            >
              <span
                v-for="dividerIndex in pockets.length"
                :key="`divider-${dividerIndex}`"
                class="roulette-disc__divider"
                :style="{
                  transform: `rotate(${(dividerIndex - 1) * segmentAngle -
                    segmentAngle / 2}deg)`
                }"
              ></span>

              <span
                v-for="(pocket, index) in pockets"
                :key="pocket.id"
                class="roulette-disc__number"
                :class="{ 'roulette-disc__number--zero': pocket.number === 0 }"
                :style="pocketStyle(index, pocket)"
              >
                {{ pocket.number }}
              </span>
            </div>

            <div class="roulette-cone">
              <div class="roulette-cone__groove"></div>
              <div class="spindle">
                <span class="spindle__arm spindle__arm--vertical"></span>
                <span class="spindle__arm spindle__arm--horizontal"></span>
                <span class="spindle__cap"></span>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>

    <div class="roulette-readout">
      <div>
        <small>EUROPEAN WHEEL</small>
        <strong>0–36</strong>
      </div>
      <span></span>
      <div>
        <small>สิทธิ์คงเหลือ</small>
        <strong>{{ remainingSpins }} ครั้ง</strong>
      </div>
    </div>

    <button
      class="roulette-action"
      type="button"
      :disabled="isSpinning || remainingSpins < 1"
      :aria-label="actionLabel"
      @click="spin"
    >
      <span class="roulette-action__ball" aria-hidden="true"></span>
      <span class="roulette-action__copy">
        <strong>{{ actionLabel }}</strong>
        <small>
          {{
            isSpinning
              ? "WAIT FOR THE BALL"
              : remainingSpins > 0
              ? "SPIN ROULETTE"
              : "COME BACK TOMORROW"
          }}
        </small>
      </span>
      <span class="roulette-action__arrow" aria-hidden="true">→</span>
    </button>
  </section>
</template>

<style lang="scss" scoped>
.roulette-stage {
  --wheel-size: min(88vw, 402px, 51vh);
  --number-radius: calc(var(--wheel-size) * 0.238);
  --rail-pin-radius: calc(var(--wheel-size) * 0.423);
  position: relative;
  z-index: 2;
  display: flex;
  width: 100%;
  flex-direction: column;
  align-items: center;
  isolation: isolate;
}

.roulette-stage__shadow {
  position: absolute;
  z-index: -1;
  top: calc(var(--wheel-size) * 0.74);
  left: 50%;
  width: calc(var(--wheel-size) * 0.86);
  height: calc(var(--wheel-size) * 0.23);
  border-radius: 50%;
  transform: translateX(-50%);
  background: rgba(0, 0, 0, 0.6);
  filter: blur(18px);
}

.roulette-machine {
  position: relative;
  width: var(--wheel-size);
  height: var(--wheel-size);
  transform: perspective(900px) rotateX(6deg);
  transform-style: preserve-3d;
}

.roulette-wood {
  position: relative;
  width: 100%;
  height: 100%;
  border: 1px solid #c59456;
  border-radius: 50%;
  background: radial-gradient(
      circle at 35% 24%,
      rgba(255, 255, 255, 0.32) 0 1%,
      transparent 2%
    ),
    repeating-conic-gradient(
      from 6deg,
      rgba(255, 255, 255, 0.05) 0deg 1deg,
      transparent 1deg 30deg
    ),
    radial-gradient(
      circle,
      #9f5f31 0%,
      #5d2a18 37%,
      #a36031 54%,
      #3b170f 75%,
      #7d3e22 91%,
      #2a0f0b 100%
    );
  box-shadow: 0 26px 42px rgba(0, 0, 0, 0.58), 0 0 0 3px rgba(51, 19, 10, 0.82),
    0 0 0 5px rgba(215, 164, 96, 0.44),
    inset 0 3px 5px rgba(255, 220, 162, 0.46),
    inset 0 -14px 24px rgba(25, 7, 5, 0.5);
}

.roulette-wood::before {
  content: "";
  position: absolute;
  z-index: 1;
  inset: 4.2%;
  border: 2px solid rgba(238, 198, 129, 0.54);
  border-radius: 50%;
  box-shadow: 0 0 0 3px #4b2113, inset 0 0 12px rgba(255, 218, 151, 0.2);
}

.roulette-wood::after {
  content: "";
  position: absolute;
  z-index: 12;
  inset: 0;
  border-radius: 50%;
  background: linear-gradient(
    132deg,
    rgba(255, 255, 255, 0.16),
    transparent 22% 68%,
    rgba(24, 7, 4, 0.28)
  );
  pointer-events: none;
}

.rail-pin {
  position: absolute;
  z-index: 14;
  top: 50%;
  left: 50%;
  width: calc(var(--wheel-size) * 0.027);
  height: calc(var(--wheel-size) * 0.027);
  margin: calc(var(--wheel-size) * -0.0135);
  border: 1px solid rgba(104, 57, 21, 0.7);
  border-radius: 2px;
  background: linear-gradient(145deg, #fff0bd, #c28a41 57%, #5e3218);
  box-shadow: 0 2px 3px rgba(0, 0, 0, 0.46);
}

.roulette-rail {
  position: absolute;
  z-index: 3;
  inset: 8.5%;
  border: 1px solid rgba(240, 205, 147, 0.52);
  border-radius: 50%;
  background: radial-gradient(
    circle,
    transparent 69%,
    #171813 70%,
    #080907 86%,
    #3b2c1b 87%,
    #100c08 100%
  );
  box-shadow: inset 0 0 22px rgba(0, 0, 0, 0.9), 0 0 0 3px rgba(54, 26, 15, 0.9);
}

.ball-orbit {
  position: absolute;
  z-index: 20;
  inset: 1.5%;
  border-radius: 50%;
  transform: rotate(var(--ball-rotation));
  transition: transform 5.8s cubic-bezier(0.08, 0.68, 0.06, 1);
  will-change: transform;
}

.roulette-ball {
  position: absolute;
  top: 4.4%;
  left: 50%;
  width: calc(var(--wheel-size) * 0.037);
  aspect-ratio: 1;
  border-radius: 50%;
  transform: translateX(-50%);
  background: radial-gradient(
    circle at 34% 28%,
    #ffffff 0 18%,
    #f7f0df 34%,
    #b0a48e 72%,
    #45413a 100%
  );
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.78), 0 0 8px rgba(255, 248, 226, 0.4);
}

.roulette-pocket-ring {
  position: absolute;
  inset: 12%;
  overflow: hidden;
  border: calc(var(--wheel-size) * 0.014) solid #b27735;
  border-radius: 50%;
  background: #0c1310;
  box-shadow: 0 0 0 2px #f0c77d, 0 0 0 5px #4f2715,
    inset 0 0 18px rgba(0, 0, 0, 0.9);
}

.roulette-disc {
  position: absolute;
  inset: 0;
  border-radius: 50%;
  transform: rotate(var(--wheel-rotation));
  transition: transform 5.8s cubic-bezier(0.08, 0.68, 0.06, 1);
  will-change: transform;
}

.roulette-disc::before,
.roulette-disc::after {
  content: "";
  position: absolute;
  z-index: 2;
  border-radius: 50%;
  pointer-events: none;
}

.roulette-disc::before {
  inset: 0;
  background: radial-gradient(
      circle at 36% 25%,
      rgba(255, 255, 255, 0.18),
      transparent 9%
    ),
    radial-gradient(
      circle,
      transparent 52%,
      rgba(0, 0, 0, 0.08) 68%,
      rgba(0, 0, 0, 0.48) 100%
    );
}

.roulette-disc::after {
  inset: 1px;
  border: 1px solid rgba(255, 229, 178, 0.3);
  box-shadow: inset 0 0 15px rgba(237, 196, 123, 0.18);
}

.roulette-disc__divider {
  position: absolute;
  z-index: 3;
  top: 0;
  left: 50%;
  width: 1px;
  height: 50%;
  transform-origin: 50% 100%;
  background: linear-gradient(
    180deg,
    rgba(244, 219, 170, 0.92),
    rgba(139, 86, 39, 0.46) 76%,
    transparent
  );
  box-shadow: 0 0 1px rgba(45, 17, 8, 0.8);
}

.roulette-disc__number {
  position: absolute;
  z-index: 4;
  top: 50%;
  left: 50%;
  display: flex;
  width: calc(var(--wheel-size) * 0.064);
  height: calc(var(--wheel-size) * 0.054);
  margin: calc(var(--wheel-size) * -0.027) 0 0 calc(var(--wheel-size) * -0.032);
  align-items: center;
  justify-content: center;
  font-family: Georgia, "Times New Roman", serif;
  font-size: calc(var(--wheel-size) * 0.027);
  font-weight: 700;
  line-height: 1;
  text-align: center;
  text-shadow: 0 1px 2px rgba(0, 0, 0, 0.78);
}

.roulette-disc__number--zero {
  color: #fff1b5 !important;
  text-shadow: 0 0 7px rgba(255, 224, 137, 0.64);
}

.roulette-cone {
  position: absolute;
  z-index: 7;
  inset: 25%;
  border: calc(var(--wheel-size) * 0.011) solid #bc813d;
  border-radius: 50%;
  background: radial-gradient(
      circle at 38% 29%,
      rgba(255, 255, 255, 0.26),
      transparent 7%
    ),
    repeating-conic-gradient(
      from 0deg,
      rgba(255, 255, 255, 0.035) 0deg 1deg,
      transparent 1deg 22.5deg
    ),
    radial-gradient(circle, #9a5b2f, #4b2116 57%, #1e0d0a 100%);
  box-shadow: 0 0 0 2px #efd08f, 0 0 0 5px #462112,
    inset 0 5px 12px rgba(255, 218, 158, 0.22),
    inset 0 -10px 16px rgba(23, 7, 5, 0.42);
}

.roulette-cone__groove {
  position: absolute;
  inset: 18%;
  border: 1px solid rgba(239, 204, 143, 0.32);
  border-radius: 50%;
  box-shadow: inset 0 0 10px rgba(0, 0, 0, 0.3);
}

.spindle {
  position: absolute;
  z-index: 10;
  inset: 22%;
  border-radius: 50%;
}

.spindle__arm {
  position: absolute;
  top: 50%;
  left: 50%;
  border: 1px solid #6a4628;
  border-radius: 999px;
  transform: translate(-50%, -50%);
  background: linear-gradient(
    90deg,
    #6d411f,
    #f3dba1 46%,
    #9b632f 58%,
    #4a2716
  );
  box-shadow: 0 3px 6px rgba(0, 0, 0, 0.54);
}

.spindle__arm::before,
.spindle__arm::after {
  content: "";
  position: absolute;
  top: 50%;
  width: calc(var(--wheel-size) * 0.035);
  aspect-ratio: 1;
  border: 1px solid #6a421f;
  border-radius: 50%;
  transform: translateY(-50%);
  background: radial-gradient(circle at 36% 30%, #fff0b8, #b87935 70%);
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.42);
}

.spindle__arm::before {
  left: calc(var(--wheel-size) * -0.012);
}

.spindle__arm::after {
  right: calc(var(--wheel-size) * -0.012);
}

.spindle__arm--horizontal {
  width: calc(var(--wheel-size) * 0.27);
  height: calc(var(--wheel-size) * 0.025);
}

.spindle__arm--vertical {
  width: calc(var(--wheel-size) * 0.27);
  height: calc(var(--wheel-size) * 0.025);
  transform: translate(-50%, -50%) rotate(90deg);
}

.spindle__cap {
  position: absolute;
  z-index: 2;
  top: 50%;
  left: 50%;
  width: calc(var(--wheel-size) * 0.095);
  aspect-ratio: 1;
  border: 1px solid #7a4b22;
  border-radius: 50%;
  transform: translate(-50%, -50%);
  background: radial-gradient(
    circle at 35% 28%,
    #fff4c6,
    #d7a258 44%,
    #7e451f 77%,
    #3b1b10
  );
  box-shadow: 0 4px 8px rgba(0, 0, 0, 0.55),
    inset 0 2px 3px rgba(255, 255, 255, 0.56);
}

.roulette-readout {
  display: flex;
  width: min(88%, 330px);
  margin-top: 13px;
  padding: 8px 15px;
  border: 1px solid rgba(227, 184, 112, 0.18);
  border-radius: 12px;
  align-items: center;
  justify-content: space-around;
  background: rgba(2, 9, 7, 0.34);
  box-shadow: inset 0 1px 0 rgba(255, 255, 255, 0.035);
}

.roulette-readout > div {
  display: flex;
  min-width: 95px;
  flex-direction: column;
  align-items: center;
  line-height: 1.15;
}

.roulette-readout small {
  color: rgba(233, 210, 169, 0.45);
  font-family: Georgia, "Times New Roman", serif;
  font-size: 7px;
  font-weight: 700;
  letter-spacing: 0.13em;
}

.roulette-readout strong {
  margin-top: 3px;
  color: #eed19a;
  font-family: "Athiti", "Kanit", sans-serif;
  font-size: 12px;
  font-weight: 700;
}

.roulette-readout > span {
  width: 1px;
  height: 24px;
  background: linear-gradient(
    180deg,
    transparent,
    rgba(226, 184, 113, 0.3),
    transparent
  );
}

.roulette-action {
  position: relative;
  display: flex;
  width: min(88%, 330px);
  margin-top: 10px;
  overflow: hidden;
  padding: 10px 13px;
  border: 1px solid #e4b56c;
  border-radius: 14px;
  align-items: center;
  gap: 11px;
  background: linear-gradient(135deg, #f0cf91, #bd7a3c 58%, #84441f);
  color: #32170c;
  box-shadow: 0 8px 18px rgba(0, 0, 0, 0.29),
    inset 0 1px 0 rgba(255, 255, 255, 0.56),
    inset 0 -2px 0 rgba(66, 28, 11, 0.25);
  text-align: left;
  transition: transform 180ms ease, filter 180ms ease;
}

.roulette-action::before {
  content: "";
  position: absolute;
  top: -45%;
  left: -40%;
  width: 26%;
  height: 190%;
  transform: rotate(24deg);
  background: rgba(255, 255, 255, 0.26);
  filter: blur(2px);
  animation: actionShine 4s ease-in-out infinite;
}

.roulette-action:active:not(:disabled) {
  transform: translateY(2px) scale(0.985);
}

.roulette-action:disabled {
  cursor: wait;
  filter: saturate(0.62) brightness(0.86);
}

.roulette-action__ball {
  width: 27px;
  height: 27px;
  flex: 0 0 27px;
  border: 1px solid rgba(79, 43, 23, 0.58);
  border-radius: 50%;
  background: radial-gradient(
    circle at 34% 28%,
    #fff 0 19%,
    #efe4cf 37%,
    #a2947e 75%,
    #50463a 100%
  );
  box-shadow: 0 3px 5px rgba(44, 19, 7, 0.34);
}

.roulette-action__copy {
  display: flex;
  flex: 1;
  flex-direction: column;
}

.roulette-action__copy strong {
  font-family: "Mali", "Kanit", sans-serif;
  font-size: 14px;
  font-weight: 700;
  line-height: 1.2;
}

.roulette-action__copy small {
  margin-top: 3px;
  font-family: Georgia, "Times New Roman", serif;
  font-size: 7px;
  font-weight: 700;
  letter-spacing: 0.14em;
  opacity: 0.65;
}

.roulette-action__arrow {
  font-family: Georgia, "Times New Roman", serif;
  font-size: 19px;
}

@keyframes actionShine {
  0%,
  64% {
    left: -40%;
  }
  84%,
  100% {
    left: 125%;
  }
}

@media (max-height: 720px) {
  .roulette-stage {
    --wheel-size: min(78vw, 350px, 44vh);
  }

  .roulette-readout {
    margin-top: 8px;
    padding-top: 6px;
    padding-bottom: 6px;
  }

  .roulette-action {
    margin-top: 7px;
    padding-top: 8px;
    padding-bottom: 8px;
  }
}

@media (orientation: landscape) and (max-height: 560px) {
  .roulette-stage {
    --wheel-size: min(55vh, 310px);
  }
}

@media (prefers-reduced-motion: reduce) {
  .roulette-action::before {
    animation: none;
  }

  .roulette-disc,
  .ball-orbit {
    transition-duration: 1.7s;
  }
}
</style>
